import { User } from '../types/shared';
import { validateCaptcha } from './validate-captcha';

type UserArgs = {
    email: string;
    password: string;
    siteCountry?: string;  // Site's country slug from Atos Globals (e.g., "sweden", "germany")
};

type LoginResult = {
    success: boolean;
    message?: string;
    policyRequired?: {
        uuid: string;
        firstName: string;
        userEmail: string;
        siteCountry: string;   // Site's country slug (e.g., "sweden")
        countryCode: string;   // ISO country code (e.g., "SE")
    };
};

export const logIn = async (userArgs: UserArgs, updateUser: (user: User) => void, recaptchaSiteKey: string, deploymentEnvironment: string, ssoBaseUrl: string): Promise<LoginResult> => {
    try {
        const captchaToken = await validateCaptcha('login_user', recaptchaSiteKey);

        if (!captchaToken) {
            throw new Error('Captcha validation failed - probably a bot.');
        }

        const response = await fetch('/login', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                ...userArgs,
                captchaToken,
            }),
        });

        if (response.ok) {
            const res = await response.json();
            if (res.success) {
                updateUser(res.user);

                // Redirect to our SSO-base to set shared SSO cookie. But not if user is already on .com
                // And dont bother in local env.
                if (ssoBaseUrl && ssoBaseUrl !== '') {
                    if (deploymentEnvironment === 'production' || deploymentEnvironment === 'stage') {
                        const hostname = window.location.hostname?.replace(/^www\./, '');
                        const isMainSite = hostname === 'atosmedical.com' || hostname === 'stage.atosmedical.24hr.se';
                        if (!isMainSite) {
                            const returnTo = encodeURIComponent(window.location.href);
                            window.location.href = `${ssoBaseUrl}/sso/set-auth-cookie?token=${res.token}&returnTo=${returnTo}`;
                        }
                    }
                }

                return { success: true };
            } else {
                if (res.message === 'user-not-verified') {
                    return { success: false, message: 'user-not-verified' };
                }
                if (res.message === 'user-not-found') {
                    return { success: false, message: 'user-not-found' };
                }
                if (res.message === 'policy-required') {
                    return {
                        success: false,
                        message: 'policy-required',
                        policyRequired: {
                            uuid: res.uuid,
                            firstName: res.firstName,
                            userEmail: res.userEmail,
                            siteCountry: res.siteCountry,
                            countryCode: res.countryCode,
                        },
                    };
                }
                return { success: false, message: 'invalid-credentials' };
            }
        } else {
            throw new Error('Invalid credentials');
        }
    } catch (error) {
        return { success: false, message: 'invalid-credentials' };
    }
};

export const acceptPrivacyPolicy = async (
    uuid: string,
    countryCode: string,
    updateUser: (user: User) => void,
    deploymentEnvironment: string,
    ssoBaseUrl: string
): Promise<{ success: boolean; message?: string }> => {
    try {
        const response = await fetch('/accept-privacy-policy', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                uuid,
                countryCode,
            }),
        });

        if (response.ok) {
            const res = await response.json();
            if (res.success) {
                updateUser(res.user);

                // Redirect to our SSO-base to set shared SSO cookie
                if (ssoBaseUrl && ssoBaseUrl !== '') {
                    if (deploymentEnvironment === 'production' || deploymentEnvironment === 'stage') {
                        const hostname = window.location.hostname?.replace(/^www\./, '');
                        const isMainSite = hostname === 'atosmedical.com' || hostname === 'stage.atosmedical.24hr.se';
                        if (!isMainSite) {
                            const returnTo = encodeURIComponent(window.location.href);
                            window.location.href = `${ssoBaseUrl}/sso/set-auth-cookie?token=${res.token}&returnTo=${returnTo}`;
                        }
                    }
                }

                return { success: true };
            } else {
                return { success: false, message: res.message };
            }
        } else {
            throw new Error('Failed to accept policy');
        }
    } catch (error) {
        return { success: false, message: 'error-accepting-policy' };
    }
};
